/**
 * Desktop (`lg` and up) view of quick entry, rendered by `../../QuickEntryV2.tsx`: one row of
 * item search, quantity + quantity UOM (`QuantityUOMV2`), price UOM and Add Below, with a close
 * icon. Adds keyboard handling the mobile view lacks: arrow keys and Enter move through and pick
 * search results, and Tab from the Add Below button loops back to the search input.
 *
 * DOM ids and classes used for focus:
 * - `quick-entry-search-input` and `button-container-add-below` are literals in this file.
 *   `../SharedComponents.tsx` → `SearchInput` applies the first (`inputId`) as the input's `id`
 *   and `data-testid`, and → `AddBelowButton` applies the second (`buttonClassName`) as its
 *   `className`. Changing how those components apply the props breaks the lookups here.
 * - `product-list` is set in `../SharedComponents.tsx` → `ProductList` (the `ul` class), and
 *   `quantity-uom-input-<id>` in `QuantityUOMV2.tsx` (looked up here with the `id` "quick-entry"
 *   passed below). Renaming either there breaks the lookups here.
 */
import React, { useState } from "react";
import { useApplication } from "@ryerson/frontend.application";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { css } from "@emotion/react";
import { QuickEntryContent, QuickEntryText } from "../QuickEntryContent";
import { QuickEntryProps } from "../QuickEntry.models";
import { Icon } from "@ryerson/frontend.assets";

import { UOMValue } from "@ryerson/common.uom";
import { SelectKeys } from "../QuickEntry.models";
import {
	ErrorMessage,
	ErrorContainer,
	QuickEntryContainer,
	LabelContainer,
} from "../StyledComponents";

import { SearchInput, ProductList, AddBelowButton, PriceUOM } from "../SharedComponents";
import { QuantityUOMV2 } from "./QuantityUOMV2";

/** Desktop quick-entry row. All state comes from `QuickEntryProps`; see the file header. */
const QuickEntryDesktopV2: React.FC<QuickEntryProps> = ({
	searchInputContainerRef,
	showDropdown,
	setShowDropdown,
	searchResult,
	onClickSelectItem,
	isError,
	quantity,
	setQuantity,
	qtyUOM,
	priceUOM,
	priceUOMDropdown,
	onClickAddBelow,
	displaySearchTerm,
	onChangeText,
	searchTerm,
	setPriceUOM,
	closeQuickEntry,
	selectedProduct,
	itemAddSuccess,
	itemAddFail,
	updateQuantityUom,
	handleValidationResponse,
	hasMounted,
	stateMessenger,
	qtyUOMDropdown,
	isQuickEntryValid,
}) => {
	const { theme } = useTheme();
	const backgroundPrimary = theme.colors.primary.background;
	const headerSecondary = theme.colors.secondary.header;
	const textSecondary = theme.colors.secondary.text;
	const { red } = theme.colors.secondary;
	const [tempItemNumber, setTempItemNumber] = useState<any>();

	// to work with arrow keys
	const [productSelectedIndex, setProductSelectedIndex] = React.useState<number>(-1);

	// Publish call to pubsub
	const publishMessage = (messageString: string, data: any, topic: any) => {
		/* note the use of setTimeout is required for publishing messages*/
		setTimeout(() => stateMessenger?.publish(messageString, data, topic), 10);
	};

	// create a useRef to tack when to cycle focus if using keys
	// `isButtonFocused` is written but not read in this view; V1 `../QuickEntryDesktop.tsx` read it
	// to refocus the button.
	const resetFocus = React.useRef<boolean>(false);
	const isButtonFocused = React.useRef<boolean>(false);

	//Note:  this name utilized in keydown even code
	const buttonClassName = "button-container-add-below";

	const {
		localization: { language },
	} = useApplication();

	const content: QuickEntryText = QuickEntryContent[language] || QuickEntryContent.en;

	React.useEffect(() => {
		if (hasMounted) {
			const _query = document && (document as any).querySelector.bind(document);
			const elInput = _query("[data-testid=quick-entry-search-input]");
			elInput?.focus();
		}
	}, [hasMounted]);

	/* This view does not listen for `is-debouncing` messages. V1 `../QuickEntryDesktop.tsx` held
		Add Below disabled while its quantity input debounced; here the button is enabled from
		`quantity` and `isQuickEntryValid` alone. */

	// Note: Utilized for scrolling the product list with arrow keys
	const setScrollTop = (amount: number) => {
		const UList = document.querySelector("ul.product-list");
		UList && (UList.scrollTop += amount);
	};

	// Note: used when using tab keys to navigate across the quick entry and loop
	// back to the search input
	const handleFocusChanged = (e: React.FocusEventHandler<HTMLDivElement> | undefined | any) => {
		if (e.target?.className?.includes(buttonClassName)) {
			isButtonFocused.current = true;
			resetFocus.current = true;
		} else {
			if (e.target?.id === "quantity-uom-input-quick-entry") {
				setShowDropdown(false);
			}
			isButtonFocused.current = false;
			resetFocus.current = false;
		}
	};
	//  Works with handleFocusChanged to set the focus on search input (loops around)
	const focusGuard = (e: React.KeyboardEventHandler<HTMLDivElement> | undefined | any) => {
		if (e.key === "Tab" && resetFocus.current === true) {
			resetFocus.current = false;
			const elInput = document?.querySelector("#quick-entry-search-input");
			setTimeout(() => {
				elInput && (elInput as any)?.blur();
				elInput && (elInput as any)?.focus();
			}, 20);
		}
	};

	/**
	 *
	 * @desc - Function to handle arrow keys for product list
	 * @param e - event
	 */
	const handleArrowKeys = (e: React.KeyboardEventHandler<HTMLDivElement> | undefined | any) => {
		if (showDropdown) {
			if (e.key === SelectKeys.ArrowDown) {
				setProductSelectedIndex((prev) => Math.min(searchResult?.length - 1, prev + 1));
				if (productSelectedIndex > 0 && productSelectedIndex < searchResult.length - 2) {
					setScrollTop(120);
				}
			} else if (e.key === SelectKeys.ArrowUp) {
				setProductSelectedIndex((prev) => Math.max(0, prev - 1));
				if (productSelectedIndex < searchResult.length - 2) {
					setScrollTop(-120);
				}
			} else if (e.key === SelectKeys.Enter) {
				const selectedProduct = searchResult[productSelectedIndex];
				if (selectedProduct) {
					onClickSelectItem(selectedProduct);
				}
			}
		}
	};
	React.useEffect(() => {
		if (productSelectedIndex >= 0) {
			setProductSelectedIndex(-1);
		}
	}, [showDropdown]);

	return (
		<>
			<div onKeyDown={focusGuard} onFocus={handleFocusChanged}>
				<QuickEntryContainer {...{ background: backgroundPrimary }}>
					<Flex alignItems="flex-start" css={{ width: "85%", padding: "17px 0 33px" }}>
						<FlexItem css={{ width: "35%" }}>
							<Flex
								itemRef={searchInputContainerRef}
								direction="column"
								style={css`
									padding-right: 10px;
								`}
							>
								<div onKeyDown={handleArrowKeys}>
									<SearchInput
										inputId="quick-entry-search-input"
										displaySearchTerm={displaySearchTerm}
										handleChange={(e) => onChangeText(e)}
										handleFocus={() => setShowDropdown(true)}
									/>
								</div>

								{showDropdown && (
									<ProductList
										searchResults={searchResult}
										handleClick={onClickSelectItem}
										selectItemIndex={productSelectedIndex}
									/>
								)}
								{isError && (
									<ErrorContainer>
										<ErrorMessage
											{...{
												bgColor: backgroundPrimary,
												bColor: textSecondary,
											}}
										>
											{"No Results Found".replace(
												"%searchString%",
												searchTerm.toString()
											)}
										</ErrorMessage>
									</ErrorContainer>
								)}
							</Flex>
						</FlexItem>
						<FlexItem>
							<Flex
								direction="column"
								style={css`
									padding-right: 10px;
								`}
							>
								<LabelContainer>
									<Typography color={headerSecondary}>
										{content.quantityUom}
									</Typography>
									<Typography color={red}> *</Typography>
								</LabelContainer>

								{/* `forceTrigger` with `recalculateQuantities={false}`: every
								keystroke is validated and reported at once, with no debounce
								and no rounding to whole pieces. `isSubmitted` is always true
								here, unlike the mobile view, which passes
								`isQuickEntrySubmitted`. */}
								<QuantityUOMV2
									id="quick-entry"
									itemData={selectedProduct || {}}
									quantityUomOptions={qtyUOMDropdown}
									size="sm"
									type="primary"
									disabled={!selectedProduct}
									dataTestId="quantity-uom-test"
									isFocused={!!selectedProduct}
									popperDisplayTime={2000}
									isSubmitted={true}
									quantityValue={quantity}
									uomValue={qtyUOM as UOMValue}
									handleChange={(quantityValue, uomValue) => {
										setQuantity(quantityValue);
									}}
									handleUOMChange={(uomValue) => {
										updateQuantityUom(uomValue);
									}}
									handleValidationResponse={handleValidationResponse}
									stateMessenger={stateMessenger}
									recalculateQuantities={false}
									forceTrigger={true}
								/>
							</Flex>
						</FlexItem>

						{/* Interaction with the price-UOM dropdown is published as `to-debounce`
						messages for topic "quick-entry", which `QuantityUOMV2` (id "quick-entry")
						receives through `stateMessenger`. */}
						<div
							css={{ width: "150px" }}
							onMouseLeave={() =>
								publishMessage(`to-debounce`, "mouseout", "quick-entry")
							}
							onFocus={() => publishMessage(`to-debounce`, "focus", "quick-entry")}
							onChange={(value) =>
								publishMessage(`to-debounce`, "change", "quick-entry")
							}
						>
							<PriceUOM
								handleChange={setPriceUOM}
								isDisabled={() => {
									return !selectedProduct || priceUOMDropdown.length === 1;
								}}
								priceUOM={priceUOM}
								priceUOMDropdown={priceUOMDropdown}
							/>
						</div>
						<FlexItem css={{ width: "130px" }}>
							<AddBelowButton
								buttonClassName={buttonClassName}
								itemNumber={tempItemNumber}
								itemAddSuccess={itemAddSuccess}
								itemAddFail={itemAddFail}
								handleClick={() => {
									setTempItemNumber(selectedProduct?.partNumber);
									onClickAddBelow(() => {
										resetFocus.current = false;
										const elInput = document?.querySelector(
											"#quick-entry-search-input"
										);
										(elInput as any)?.focus();
									});
								}}
								isDisabled={!quantity || !isQuickEntryValid}
							/>
						</FlexItem>
					</Flex>
					<Flex
						css={{
							width: "15%",
							height: "100%",
							paddingTop: "15px",
							paddingRight: "18px",
						}}
						justifyContent="flex-end"
						alignItems="flex-start"
					>
						<Icon
							onClick={closeQuickEntry}
							icon="close"
							color={textSecondary}
							circled={true}
							dataTestId="shopping-list-details-quick-entry-close"
						/>
					</Flex>
				</QuickEntryContainer>
			</div>
		</>
	);
};

export default QuickEntryDesktopV2;
